import React, { useState,useMemo, useRef, useEffect } from 'react';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { useSearchParams } from 'react-router-dom';
import { usePendingAddress } from '../../features/post-order-address/presentation/hooks/usePendingAddress';
import { Skeleton } from '@/shared/components/Skeleton';
import { useToast } from '@/shared/widgets/toast/useToast';
import { Input } from '@/shared/widgets/input';
import { Button } from '@/shared/widgets/button';
import { ChevronDown, Check, CheckCircle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/widgets/dropdown-menu';
import { cn } from '@/shared/utils/cn';
import { validateAddressForm } from '@/shared/utils/validation';
import type { AddressDetails, StateOption } from '../../features/post-order-address/domain/models/postOrderAddress.types';
import { buildPostOrderAddress } from '../../features/post-order-address/config/postOrderAddress.build';
import { OrderSummary } from '@/features/post-order-address/presentation/components/OrderSummary';

type FormErrors = Partial<Record<keyof AddressDetails, string>>;

type FormFieldConfig = {
  name: keyof AddressDetails;
  label: string;
  required?: boolean;
  type?: React.HTMLInputTypeAttribute;
  maxLength?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  colSpan?: number;
};

const FORM_FIELDS: FormFieldConfig[] = [
  { name: 'fullName', label: 'Full Name' },
  { name: 'contactNumber', label: 'Contact Number', type: 'tel', maxLength: 10, inputMode: 'numeric' },
  { name: 'address', label: 'Address / Flat No.', colSpan: 2 },
  { name: 'street', label: 'Street' },
  { name: 'locality', label: 'Locality' },
  { name: 'landmark', label: 'Landmark', required: false },
  { name: 'postCode', label: 'Pincode', type: 'tel', maxLength: 6, inputMode: 'numeric' },
  { name: 'cityName', label: 'City' },
];

const FormSkeleton: React.FC = () => (
  <div className="max-w-4xl mx-auto p-4 md:p-8">
    <Skeleton className="h-7 w-64 mb-6" />
    <div className="border rounded-md p-4 mb-6 bg-white">
      <Skeleton className="h-7 w-64 mb-6" />
      <div className="space-y-5">        
        <div className="flex gap-4">
          <Skeleton className="h-16 w-16 rounded" />
          <div className="flex-1">
            <div className="flex justify-between gap-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-16 shrink-0" />
            </div>
            <Skeleton className="h-3 w-24 mt-2" />
            <Skeleton className="h-3 w-32 mt-2" />
          </div>
        </div>
      </div>
    </div>
    <div className="border rounded p-4 md:p-6 bg-white">
      <Skeleton className="h-6 w-1/4 mb-6" />
      <div className="flex flex-col md:grid md:grid-cols-2 gap-6 gap-y-10">
        {[...Array(9)].map((_, i) => (
          <Skeleton key={i} className={cn("h-10 w-full", i === 2 && "md:col-span-2")} />
        ))}
      </div>
    </div>
  </div>
);

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
}

const Field: React.FC<FieldProps> = ({ label, error, required = true, className, ...props }) => (

  <div className="flex flex-col">
    <label className="text-sm text-gray-600 mb-1">
      {label} {required && <span className="text-[#F15D47]">*</span>}
    </label>
    <Input 
      required={required}
      className={cn(
        "w-full border-gray-300 rounded focus-visible:ring-offset-0",
        error && "border-red-500 focus-visible:ring-red-500",
        className
      )}
      {...props}
    />
    <div className="h-5 mt-1">
      {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
    </div>
  </div>
);

interface AddressFormGroupProps {
  title: string;
  address: AddressDetails;
  stateOptions: StateOption[];
  errors: FormErrors;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onStateChange: (stateId: string) => void;
  onPincodeBlur: (pincode: string) => void;
  disabled?: boolean;
}

const AddressFormGroup: React.FC<AddressFormGroupProps> = ({ 
  title, address, stateOptions, errors, onChange, onStateChange, onPincodeBlur, disabled 
}) => {
  const selectedState = stateOptions.find(s => String(s.id) === String(address.stateId));

  return (
    <div className="border rounded-b p-4 md:p-6 bg-white">
      <p className="text-md font-semibold mb-4 border-b pb-2">{title}</p>
      <div className="flex flex-col md:grid md:grid-cols-2 gap-x-4 gap-y-0">
        
        {FORM_FIELDS.map(f => (
          <div key={f.name} className={f.colSpan === 2 ? 'md:col-span-2' : undefined}>
            <Field 
              label={f.label}
              name={f.name}
              value={address[f.name] || ''}
              onChange={onChange}
              onBlur={f.name === 'postCode' ? (e: React.FocusEvent<HTMLInputElement>) => onPincodeBlur(e.target.value) : undefined}
              disabled={disabled}
              error={errors[f.name]}
              required={f.required !== false}
              type={f.type}
              maxLength={f.maxLength}
              inputMode={f.inputMode}
            />
          </div>
        ))}
        
        <div className="flex flex-col">
          <label className="text-sm text-gray-600 mb-1">State <span className="text-[#F15D47]">*</span></label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={disabled}>
              <button 
                type="button" 
                className={cn(
                  "flex h-10 w-full items-center justify-between rounded border border-gray-300 bg-white px-3 py-2 text-sm focus-visible:ring-1 focus-visible:ring-[#F15D47]", 
                  errors.stateId && "border-red-500", 
                  disabled && "cursor-not-allowed bg-gray-100 opacity-50"
                )}
              >
                <span className={!selectedState ? "text-gray-500" : "truncate"}>
                  {selectedState ? selectedState.name : "Select State"}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-gray-600" aria-hidden />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[12rem] max-h-60 overflow-y-auto bg-white">
              {stateOptions.map(state => (
                <DropdownMenuItem 
                  key={state.id} 
                  className="flex cursor-pointer items-center justify-between gap-3 py-2.5 pl-3 pr-2" 
                  onSelect={() => onStateChange(String(state.id))}
                >
                  <span>{state.name}</span>
                  <Check className={cn("h-4 w-4 shrink-0", String(state.id) === String(address.stateId) ? "text-[#F15D47]" : "text-transparent")} />
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="h-5 mt-1">
            {errors.stateId && <p className="text-xs text-red-600" role="alert">{errors.stateId}</p>}
          </div>
        </div>

      </div>
    </div>
  );
};

export default function PendingAddressPage() {
  const [searchParams] = useSearchParams();
  const orderCode = searchParams.get('orderCode');
  const orderToken = searchParams.get('orderToken');
  
  const { service } = useMemo(() => buildPostOrderAddress(), []);
  const { data, isLoading, error: fetchError, isSubmitting, verifyPincode, submitAddress } = usePendingAddress(service, { orderCode, orderToken });
  const { toast } = useToast();

  const { analytics, isInitialized: isAnalyticsInitialized } = useAnalytics();
  const hasTrackedViewRef = useRef(false);

  useEffect(() => {
    if (hasTrackedViewRef.current) return;
    if (!analytics || !isAnalyticsInitialized) return;

    try {
      analytics.trackEvent('Post Checkout Address Page Viewed');
      hasTrackedViewRef.current = true;
    } catch (error) {
      console.error('Error tracking Post Checkout Address Page Viewed:', error);
    }
  }, [analytics, isAnalyticsInitialized]);
  
  const [shipping, setShipping] = useState<AddressDetails | null>(null);
  const [billing, setBilling] = useState<AddressDetails | null>(null);
  const [sameAsShipping, setSameAsShipping] = useState<boolean>(true);
  const [shippingErrors, setShippingErrors] = useState<FormErrors>({});
  const [billingErrors, setBillingErrors] = useState<FormErrors>({});
  const [isInitialized, setIsInitialized] = useState<boolean>(false);


  if (data && !isInitialized) {
    setShipping(data.shippingAddress);
    setBilling(data.billingAddress);
    setIsInitialized(true);
  }


  const handlePincodeBlur = async (pincode: string, type: 'shipping' | 'billing') => {
    if (pincode.length < 6) return;
    const res = await verifyPincode(pincode);
    
    if (res && res.isPincodeValid && !res.hasError) {
      const stateIdStr = res.id_state?.toString() || '';
      const updater = (prev: AddressDetails | null) => prev ? { ...prev, cityName: res.city, stateId: stateIdStr } : prev;
      const errorClearer = (prev: FormErrors) => ({ ...prev, postCode: undefined, cityName: undefined, stateId: undefined });
      
      if (type === 'shipping') {
        setShipping(updater);
        setShippingErrors(errorClearer);
      } else {
        setBilling(updater);
        setBillingErrors(errorClearer);
      }
    } else {
      const setErr = type === 'shipping' ? setShippingErrors : setBillingErrors;
      setErr(prev => ({ ...prev, postCode: "Invalid Pincode." }));
    }
  };

  const updateAddressField = (name: keyof AddressDetails, rawValue: string, type: 'shipping' | 'billing') => {
    let value = rawValue;

    if (name === 'contactNumber' || name === 'postCode') value = value.replace(/\D/g, ""); 
    if (name === 'fullName' || name === 'cityName') value = value.replace(/[^a-zA-Z\s]/g, ""); 
    if (name === 'contactNumber') value = value.slice(0, 10);
    if (name === 'postCode') value = value.slice(0, 6);

    if (type === 'shipping') {
      setShipping(prev => prev ? { ...prev, [name]: value } : prev);
      setShippingErrors(prev => ({ ...prev, [name]: undefined }));
    } else {
      setBilling(prev => prev ? { ...prev, [name]: value } : prev);
      setBillingErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'shipping' | 'billing') => {
    updateAddressField(e.target.name as keyof AddressDetails, e.target.value, type);
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!shipping || !data) return;

    const shipValidation = validateAddressForm(shipping);
    const billValidation = sameAsShipping ? { success: true, errors: {} } : validateAddressForm(billing);

    if (!shipValidation.success || !billValidation.success) {
      setShippingErrors(shipValidation.errors);
      setBillingErrors(billValidation.errors);
      toast({ variant: 'destructive', title: 'Validation Error', description: 'Please fill all required fields.' });
      return;
    }

    const isSuccess = await submitAddress({
      orderCode: data.orderCode,
      orderToken: data.orderToken,
      shippingAddress: shipping,
      billingAddress: sameAsShipping ? shipping : billing!,
      sameAsShipping
    });
    
    if (isSuccess) {
      try {
        analytics?.trackEvent('Post Checkout Address Submitted');
      } catch (error) {
        console.error('Error tracking Post Checkout Address Submitted:', error);
      }
      window.location.href = '/account/orderhistory';
    }
  };

  if (isLoading || !isInitialized) return <FormSkeleton />;
  if (fetchError || !data || !shipping || !billing) return <div className="p-8 text-center text-[#F15D47] font-medium">{fetchError || "Data error."}</div>;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8">
      {orderToken ? <>
        <div className="flex items-center mb-2 text-2xl">
          <CheckCircle className="w-6 h-6 mr-2" color="green" fill="white" />
          Congratulations!
        </div>
        <p className='text-gray-500 mb-5'>Your order has been placed successfully.</p>
      </> : 
        <div className="flex items-center mb-5 text-2xl">
          <CheckCircle className="self-start shrink-0 w-6 h-6 mr-2 mt-1" color="green" fill="white" />
          Your order has been placed.
        </div>
      }
      <OrderSummary items={data.orderItems} />
      
      <form onSubmit={onSubmit} noValidate>
        <AddressFormGroup 
          title="Shipping Address" 
          address={shipping} 
          stateOptions={data.stateOptions} 
          errors={shippingErrors} 
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(e, 'shipping')}
          onStateChange={(stateId: string) => updateAddressField('stateId', stateId, 'shipping')}
          onPincodeBlur={(p: string) => handlePincodeBlur(p, 'shipping')} 
          disabled={isSubmitting} 
        />

        <label className="text-blue-600 my-6 flex items-start gap-2 bg-white cursor-pointer">
          <input 
            type="checkbox" 
            checked={sameAsShipping} 
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => { 
              setSameAsShipping(e.target.checked); 
              if (e.target.checked) setBillingErrors({}); 
            }} 
            className="w-4 h-4 mt-0.5 accent-[#F15D47]" 
            disabled={isSubmitting} 
          />
          <span className="text-sm select-none text-gray-800">Billing address is same as shipping address</span>
        </label>

        <div className={cn("grid transition-all duration-300", !sameAsShipping ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0")}>
          <div className="overflow-hidden">
            <AddressFormGroup 
              title="Billing Address" 
              address={billing} 
              stateOptions={data.stateOptions} 
              errors={billingErrors} 
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(e, 'billing')} 
              onStateChange={(stateId: string) => updateAddressField('stateId', stateId, 'billing')}
              onPincodeBlur={(p: string) => handlePincodeBlur(p, 'billing')} 
              disabled={isSubmitting} 
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white py-4 flex flex-col md:flex-row gap-4 md:justify-end">
          <Button type="submit" isLoading={isSubmitting} disabled={isSubmitting} className="w-full md:w-auto px-8 py-3 bg-[#F15D47] hover:bg-[#e24f3f] text-white rounded font-medium disabled:opacity-50 border-0">
            Confirm Address
          </Button>
        </div>
      </form>
    </div>
  );
}